<!-- 
Dashboard widget showing notices sent over the last week as a stacked bar chart.

The widget has two modes: one showing all notice types and the other just showing notices that the system sent automatically.
-->
<template>
  <v-flex xs12 md6 lg4 xl3>
    <v-card flat class="home">
      <our-widget-toolbar :title="title" @refresh="forceReload" @remove="$emit('removeWidget')" @toggleChartType="$emit('toggle')" />
      <our-busy-indicator :show="loading"/>
      <v-card-text class="pb-3">
        <bar-chart 
          v-if="!isChartEmpty(chartData)" ref="chart" :chart-data="chartData" :chart-options="chartOptions" 
          style="height: 265px"
        />
        <our-chart-is-empty-placeholder v-if="isChartEmpty(chartData)" style="height: 265px" />
      </v-card-text>
    </v-card>
  </v-flex>
</template>

<script>
import _ from "underscore";
import moment from "moment";
import BarChart from "@/components/common/chart/BarChart.vue";
import lookupMixins from "@/mixins/lookup.mixins";
import * as dashboardAction from "@/store/dashboard/action-types";
import * as action from "@/store/notices/action-types";
import * as mutation from "@/store/notices/mutation-types";
import chartMixins from "@/mixins/chart.mixins";

export default {
  components: { BarChart },
  mixins: [lookupMixins, chartMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    toggled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: this.chartClick,
        onHover: this.setChartHoverCursor,
        tooltips: {
          callbacks: {
            label: this.getTooltipLabel,
            title: this.getTooltipTitle,
            afterBody: this.getTooltipFooter
          }
        },
        scales: {
          xAxes: [{ stacked: true }],
          yAxes: [{ stacked: true, ticks: { min: 0 }, afterBuildTicks: this.setYAxesTicks }]
        }
      };
    },
    title() {
      return this.toggled ? "Automatic notices" : "Recent notices";
    },
    state() {
      return this.$store.state.dashboard.noticeCounts;
    },
    loading() {
      return this.state.loading;
    },
    loaded() {
      return this.state.loaded;
    },
    noticeTypes() {
      return this.toggled
        ? ["PaymentDueReminderNotice", "PaymentOverdueWarningNotice"]
        : _(this.lookups.noticeTypes).pluck("value");
    },
    chartData() {
      if (!this.loaded) return null;
      const labels = _(_(this.state.data).pluck("day")).map(d => d.substr(0, 3));
      const noticeCounts = _(this.state.data).pluck("notices");
      const datasets = [];
      for (let noticeType of this.noticeTypes) {
        const counts = _(noticeCounts).map(nc => {
          const el = _(nc).find(c => c.type == noticeType);
          return el ? el.count : 0;
        });
        datasets.push({
          label: this.lookups.noticeTypeDescriptions[noticeType],
          backgroundColor: this.colours[noticeType],
          hoverBackgroundColor: this.colours[noticeType],
          data: counts
        });
      }
      return { labels, datasets };
    },
    colours() {
      return {
        EarlyAdviceNotice: "#4CAF50",
        InformationSessionsNotice: "#8BC34A",
        EstimatedLevyNotice: "#CDDC39",
        InvoiceNotice: "#009688",
        PaymentDueReminderNotice: "#FF9800",
        PaymentOverdueWarningNotice: "#F44336",
        AdhocNotice: "#9C27B0"
      };
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    },
    toggled() {
      this.drawChart();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    forceReload() {
      this.$store.dispatch(dashboardAction.NOTICE_COUNTS_GET, this.levyCollectionId);
    },
    refresh() {
      this.$store.dispatch(dashboardAction.NOTICE_COUNTS_REFRESH, this.levyCollectionId);
      if (this.loaded) {
        this.drawChart();
      }
    },
    drawChart() {
      this.$nextTick(() => {
        if (this.$refs.chart) this.$refs.chart.display();
      });
    },
    removeWidget() {
      this.$emit("removeWidget");
    },
    chartClick(e) {
      if (this.getNoticeCount(e) > 0) {
        const index = this.getHoverIndex(e);
        const d = moment(this.state.data[index].date).format("YYYY-MM-DD");
        const criteria = { fromDate: d, toDate: d, type: this.toggled ? this.noticeTypes : [] };
        this.$store.commit(mutation.LIST_CLEAR_CRITERIA);
        this.$store.commit(mutation.LIST_SET_FILTERS, { levyCollectionId: this.levyCollectionId });
        this.$store.commit(mutation.LIST_SET_CRITERIA, criteria);
        this.$store.dispatch(action.LIST);
        this.$router.push(`/levy-collections/${this.levyCollectionId}/notices`);
      }
    },
    setChartHoverCursor(e) {
      const chartObject = this.$refs.chart.$data._chart;
      chartObject.canvas.style.cursor = this.getNoticeCount(e) > 0 ? "pointer" : "default";
    },
    getNoticeCount(e) {
      return this.getNoticeCountForIndex(this.getHoverIndex(e));
    },
    getNoticeCountForIndex(index) {
      const notices = _(this.state.data[index].notices).filter(n => this.noticeTypes.includes(n.type));
      return _(notices).reduce((total, num) => total + num.count, 0);
    },
    getHoverIndex(e) {
      const chartObject = this.$refs.chart.$data._chart;
      const activePoints = chartObject.getElementsAtXAxis(e);
      return activePoints[0]["_index"];
    },
    getTooltipLabel(tooltipItem, data) {
      const number = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
      const suffix = number > 1 ? "s" : "";
      return number > 0 ? `${number} ${data.datasets[tooltipItem.datasetIndex].label.toLowerCase()}${suffix}` : null;
    },
    getTooltipTitle(tooltipItems) {
      const index = _(tooltipItems).first().index;
      const date = moment(this.state.data[index].date);
      return date.format("dddd Do MMMM");
    },
    getTooltipFooter(tooltipItems) {
      const index = _(tooltipItems).first().index;
      return this.getNoticeCountForIndex(index) > 0 ? ["", "(click to show)"] : null;
    },
    setYAxesTicks(axis) {
      return _(axis.ticks).filter(t => t == Math.trunc(t));
    }
  }
};
</script>
